import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { canWriteAtom } from '@/atoms/session'
import { unwrappedFollowListAtom, follow, unfollow } from '@/atoms/lists'
import { currentPubkeyAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon } from '@hugeicons/core-free-icons'
import { useAtomValue } from 'jotai'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/error'

export default function FollowButton({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const accountPubkey = useAtomValue(currentPubkeyAtom)
  const canWrite = useAtomValue(canWriteAtom)
  const followList = useAtomValue(unwrappedFollowListAtom)
  const [updating, setUpdating] = useState(false)
  const [hover, setHover] = useState(false)
  const isFollowing = useMemo(() => followList?.includes(pubkey) ?? false, [followList, pubkey])

  if (!accountPubkey || (pubkey && pubkey === accountPubkey)) return null

  const handleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isFollowing) return

    setUpdating(true)
    try {
      await follow(pubkey)
    } catch (error) {
      console.warn('follow failed:', getErrorMessage(error))
      toast.error(t('Follow failed: {{error}}', { error: getErrorMessage(error) }))
    } finally {
      setUpdating(false)
    }
  }

  const handleUnfollow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isFollowing) return

    setUpdating(true)
    try {
      await unfollow(pubkey)
    } catch (error) {
      console.warn('unfollow failed:', getErrorMessage(error))
      toast.error(t('Unfollow failed: {{error}}', { error: getErrorMessage(error) }))
    } finally {
      setUpdating(false)
    }
  }

  return isFollowing ? (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          className="rounded-full min-w-28"
          variant={hover ? 'destructive' : 'secondary'}
          disabled={updating || !canWrite}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          {updating ? (
            <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
          ) : hover ? (
            t('Unfollow')
          ) : (
            t('Following')
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('Unfollow')}?</AlertDialogTitle>
          <AlertDialogDescription>
            {t('Are you sure you want to unfollow this user?')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleUnfollow} variant="destructive">
            {t('Unfollow')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ) : (
    <Button
      className="rounded-full min-w-28"
      onClick={handleFollow}
      disabled={updating || !canWrite}
    >
      {updating ? <HugeiconsIcon icon={Loading03Icon} className="animate-spin" /> : t('Follow')}
    </Button>
  )
}
